import React, { useCallback, useMemo } from 'react';
import Router, { useRouter } from 'next/router';
import type { Maybe } from '@soundxyz/utils';
import { FeedPostModal } from '@components/modals/FeedPostModal';
import { UnlockFeedModal } from '@components/modals/FeedPostModal/UnlockFeedModal';
import { useAuth } from '@context/auth/base';
import { useConnectedAuth } from '@context/auth/connected';
import { ModalType, useModal } from '@context/modal';
import { NAV_LINKS } from '@utils/constants';
import { useBackendUserPersistedData } from './useBackendUserPersistedData';

export const useCreatePost = ({ onCreate }: { onCreate: Maybe<() => void> }) => {
  const { isAuthenticatedWithAPI } = useAuth();
  const { login } = useConnectedAuth();

  const { openModal } = useModal();
  const { hasFeedAccess } = useBackendUserPersistedData();
  const hasPostingAccess = useMemo(() => hasFeedAccess.type === 'has-access', [hasFeedAccess.type]);
  const { pathname } = useRouter();

  const isFeedPage = pathname === NAV_LINKS.Feed;
  const timeoutMs = isFeedPage ? 0 : 1000;

  const onCreatePostClick = useCallback(async () => {
    if (!isAuthenticatedWithAPI) {
      const loginResult = await login();
      if (loginResult.status !== 'success') {
        return;
      }
      Router.push(NAV_LINKS.Feed);
    } else {
      Router.push(NAV_LINKS.Feed);
      if (hasPostingAccess) {
        setTimeout(() => {
          openModal(ModalType.FEED_POST, {
            body: (
              <FeedPostModal
                key="post-input"
                sourceLocation="Feed Post Input"
                preselectedResource={null}
                channelId={null}
              />
            ),
          });
        }, timeoutMs);
      } else {
        setTimeout(() => {
          openModal(ModalType.UNLOCK_FEED_POST, {
            body: (
              <UnlockFeedModal
                key="unlock-feed-post"
                sourceLocation="Feed Post Input"
                preselectedResource={null}
              />
            ),
          });
        }, timeoutMs);
      }
      onCreate?.();
    }
  }, [hasPostingAccess, isAuthenticatedWithAPI, login, onCreate, openModal, timeoutMs]);

  return { onCreatePostClick };
};
